import { Dispatch, SetStateAction, useState } from "react";
import Highlighter from "react-highlight-words";
import Edit from "@mui/icons-material/Edit";
import { Box, Checkbox, styled, TableCell, TableRow, Tooltip, IconButton } from "@mui/material";
import MarkdownModal from "datasets/components/MarkdownModal";
import useDatasetPermission from "datasets/hooks/useDatasetPermission";
import useDatasetTable from "datasets/hooks/useDatasetTable";
import MarkdownCollapse from "datasets/pages/tables/components/MarkdownCollapse";
import TagsAutocomplete from "datasets/pages/tables/components/TagsAutocomplete";
import useErrorToast from "~/contexts/error-toast";
import { Column } from "~/types/interfaces/catalogResponse";
import rumClient from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

const StyledTBodyCell = styled(TableCell)({
  padding: "20px 8px",
  position: "relative",
  "&:first-of-type": {
    paddingLeft: 52,
  },
  "&:last-of-type": {
    paddingRight: 16,
  },
});

const DatasetTableRow = ({
  autoExpand,
  column,
  highlightText,
  selectedFields,
  setSelectedFields,
}: {
  autoExpand: boolean | null;
  column: Column;
  highlightText: string;
  selectedFields: Column[];
  setSelectedFields: Dispatch<SetStateAction<Column[]>>;
}) => {
  const { showError } = useErrorToast();
  const { data: datasetTable, mutate } = useDatasetTable();
  const canEdit = useDatasetPermission("edit:catalog-metadata");
  const columnTags = column.tags.map((tag) => tag.tagFQN);
  const [columnDescriptionModalOpen, setColumnDescriptionModalOpen] = useState(false);
  const [columnDescriptionError, setColumnDescriptionError] = useState("");

  const [isUpdating, setIsUpdating] = useState(false);
  const [showDescriptionEditButton, setShowDescriptionEditButton] = useState(false);

  const handleUpdateColumn = async <T extends keyof Column>({
    field,
    value,
    setError,
    onSuccess,
  }: {
    field: T;
    value: Column[T];
    setError: (error: string) => void;
    onSuccess?: () => void;
  }) => {
    setIsUpdating(true);

    try {
      mutate(
        (data) => {
          if (!data) {
            return data;
          }
          return {
            ...data,
            columns: data.columns.map((c) =>
              c.name === column.name ? { ...c, [field]: value } : c
            ),
          };
        },
        { revalidate: false }
      );

      const resp = await fetch(
        `${VITE_CATALOG_API}/tables/fqn/${datasetTable?.fullyQualifiedName}/column/${column.name}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ [field]: value }),
        }
      );

      mutate();

      if (!resp.ok) {
        throw new Error(`Unable to update ${field} for ${column.name}`);
      } else {
        onSuccess?.();
      }
    } catch (e: any) {
      console.error(e);
      rumClient?.recordError(e);
      const errorMessage = e.message;
      setError(errorMessage);
    }
    setIsUpdating(false);
  };

  const handleUpdateColumnDescription = (description: string) =>
    handleUpdateColumn({
      field: "description",
      value: description,
      setError: setColumnDescriptionError,
      onSuccess: () => setColumnDescriptionModalOpen(false),
    });

  const handleUpdateColumnTags = (tags: string[]) => {
    const formattedTags = tags.map((tag) => ({ tagFQN: tag }));
    handleUpdateColumn({
      field: "tags",
      value: formattedTags,
      setError: showError,
    });
  };

  return (
    <TableRow
      sx={{
        bgcolor: selectedFields
          .map(({ fullyQualifiedName }) => fullyQualifiedName)
          .includes(column.fullyQualifiedName)
          ? "primary.light"
          : "initial",
        verticalAlign: "top",
        td: {
          overflowWrap: "break-word",
          wordBreak: "normal",
        },
        "&:last-of-type td": { borderBottom: "unset" },
      }}
    >
      {/*
       * Name
       */}
      <StyledTBodyCell>
        <Box sx={{ position: "absolute", left: 7, top: 20 }}>
          <Checkbox
            name={column.fullyQualifiedName}
            size="small"
            sx={{ p: 1 }}
            checked={selectedFields
              .map(({ fullyQualifiedName }) => fullyQualifiedName)
              .includes(column.fullyQualifiedName)}
            onChange={(_, checked) => {
              if (checked) {
                setSelectedFields([...selectedFields, column]);
              } else {
                setSelectedFields(
                  selectedFields.filter(
                    (selectedField) =>
                      selectedField.fullyQualifiedName !== column.fullyQualifiedName
                  )
                );
              }
            }}
          />
        </Box>
        <Box minHeight={36} display="flex" alignItems="center">
          <Highlighter
            autoEscape
            highlightClassName="highlight"
            searchWords={[highlightText]}
            textToHighlight={column.name}
          />
        </Box>
      </StyledTBodyCell>

      {/*
       * Datatype
       */}
      <StyledTBodyCell sx={{ fontFamily: "monospace" }}>
        <Box minHeight={36} display="flex" alignItems="center">
          {column.dataType}
        </Box>
      </StyledTBodyCell>

      {/*
       * Description
       */}
      <StyledTBodyCell
        sx={{ width: "100%" }}
        onMouseEnter={() => {
          if (canEdit) {
            setShowDescriptionEditButton(true);
          }
        }}
        onMouseLeave={() => {
          if (canEdit) {
            setShowDescriptionEditButton(false);
          }
        }}
      >
        <Box display="flex" gap={1} alignItems="center">
          <MarkdownCollapse
            autoExpand={autoExpand}
            editable={canEdit}
            value={column.description}
            maxLines={3}
          />
          {showDescriptionEditButton && (
            <>
              <Tooltip arrow title="Edit description" enterDelay={500}>
                <IconButton
                  sx={{
                    width: 36,
                    height: 36,
                    position: "absolute",
                    right: 0,
                    top: 20,
                  }}
                  onClick={() => setColumnDescriptionModalOpen(true)}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              {columnDescriptionModalOpen && (
                <MarkdownModal
                  error={columnDescriptionError}
                  loading={isUpdating}
                  onClose={() => setColumnDescriptionModalOpen(false)}
                  onSubmit={handleUpdateColumnDescription}
                  open={columnDescriptionModalOpen}
                  title="Edit Column Description"
                  value={column.description}
                />
              )}
            </>
          )}
        </Box>
      </StyledTBodyCell>

      {/*
       * Tags
       */}
      <StyledTBodyCell>
        <Box minHeight={36} display="flex" alignItems="center">
          <TagsAutocomplete
            disabled={isUpdating}
            readOnly={!canEdit}
            onSave={(newVals) => handleUpdateColumnTags(newVals)}
            value={columnTags}
          />
        </Box>
      </StyledTBodyCell>
    </TableRow>
  );
};

export default DatasetTableRow;
